import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Loader2, Search, FileText, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { listServiceJobs, patchServiceJob, createServiceJob, createForm } from "@/lib/dashboardApi";
import { api } from "@/lib/api";

const TECHS = ["", "Todd", "Jordan", "Josh", "Adam"];
const STATUSES = ["Unassigned", "Scheduled", "En Route", "On Site", "Completed", "Invoiced"];
const STATUS_COLOR = {
  Unassigned: "bg-slate-100 text-slate-600", Scheduled: "bg-blue-100 text-blue-700",
  "En Route": "bg-indigo-100 text-indigo-700", "On Site": "bg-amber-100 text-amber-700",
  Completed: "bg-green-100 text-green-700", Invoiced: "bg-[#F97316]/15 text-[#F97316]",
};
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const FieldService = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { setJobs(await listServiceJobs()); }
    catch { toast.error("Could not load jobs."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, field, value) => {
    setSavingId(id);
    setJobs((p) => p.map((j) => (j.id === id ? { ...j, [field]: value } : j)));
    try { await patchServiceJob(id, { [field]: field === "job_value" ? parseFloat(value) || 0 : value }); }
    catch { toast.error("Update failed"); load(); }
    finally { setSavingId(null); }
  };

  const addJob = async () => {
    setCreating(true);
    try { await createServiceJob({ customer_name: "New Job", status: "Unassigned", job_type: "Service Call", priority: "Standard" }); toast.success("Job added"); load(); }
    catch { toast.error("Could not add job"); }
    finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    setJobs((p) => p.filter((j) => j.id !== id));
    try { await api.delete(`/servicejobs/${id}`); toast.success("Deleted"); } catch { load(); }
  };

  const toInvoice = async (job) => {
    setSavingId(job.id);
    try {
      const form = await createForm({
        customer_name: job.customer_name || "Customer", form_type: "invoice", title: "Invoice",
        data: {
          bill_to_name: job.customer_name || "", bill_to_address: job.address || "",
          line_items: [{ description: job.job_type || job.service || "HVAC Service", qty: 1, unit_price: job.job_value || "", _uid: `li_${Date.now()}` }],
          tax_rate: 0,
        },
      });
      await patchServiceJob(job.id, { status: "Invoiced", invoice_id: form.id });
      toast.success("Invoice created — opening Forms");
      navigate("/admin/forms");
    } catch { toast.error("Could not create invoice"); }
    finally { setSavingId(null); }
  };

  const filtered = useMemo(() => jobs.filter((j) => `${j.customer_name} ${j.job_type} ${j.assigned_tech} ${j.status}`.toLowerCase().includes(q.toLowerCase())), [jobs, q]);
  const sel = "border border-slate-300 rounded-md px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-[#F97316]";

  return (
    <div className="space-y-4" data-testid="field-service">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-navy flex items-center gap-2"><Wrench className="w-6 h-6 text-[#F97316]" /> Field Service <span className="text-slate-400 text-lg font-normal">({jobs.length})</span></h1>
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs…" className="pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#F97316]/20" data-testid="fs-search" /></div>
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
          <button onClick={addJob} disabled={creating} className="px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:opacity-90 disabled:opacity-60" data-testid="fs-add">+ New Job</button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Customer</th>
                <th className="text-left px-4 py-3 font-bold">Job</th>
                <th className="text-left px-4 py-3 font-bold">Technician</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Job $</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-14 text-slate-400 text-sm">No jobs yet — add one or schedule from Dispatch.</td></tr>
              ) : filtered.map((j) => (
                <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`fs-row-${j.id}`}>
                  <td className="px-4 py-3">
                    <input defaultValue={j.customer_name || ""} onBlur={(e) => e.target.value !== j.customer_name && patch(j.id, "customer_name", e.target.value)} className="font-semibold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#F97316] focus:outline-none w-full" />
                    {j.address && <p className="text-xs text-slate-400 mt-0.5">{j.address}</p>}
                  </td>
                  <td className="px-4 py-3"><input defaultValue={j.job_type || j.service || ""} onBlur={(e) => patch(j.id, "job_type", e.target.value)} className="text-slate-600 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#F97316] focus:outline-none w-32" /></td>
                  <td className="px-4 py-3">
                    <select value={j.assigned_tech || ""} onChange={(e) => patch(j.id, "assigned_tech", e.target.value)} className={sel} data-testid={`fs-tech-${j.id}`}>
                      {TECHS.map((t) => <option key={t} value={t}>{t || "Unassigned"}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select value={j.status || "Unassigned"} onChange={(e) => patch(j.id, "status", e.target.value)} className={`${sel} font-semibold`} data-testid={`fs-status-${j.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLOR[j.status] || STATUS_COLOR.Unassigned}`}>{j.status || "Unassigned"}</span>
                  </td>
                  <td className="px-4 py-3"><input type="number" defaultValue={j.job_value || ""} placeholder="0" onBlur={(e) => (parseFloat(e.target.value) || 0) !== (j.job_value || 0) && patch(j.id, "job_value", e.target.value)} className="w-24 border border-slate-200 rounded px-2 py-1 text-xs" /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {j.status === "Completed" && (
                        <button onClick={() => toInvoice(j)} disabled={savingId === j.id} className="flex items-center gap-1 text-[#F97316] hover:bg-[#F97316]/10 px-2 py-1.5 rounded-md text-xs font-semibold" data-testid={`fs-invoice-${j.id}`}>
                          <FileText className="w-3.5 h-3.5" /> Invoice
                        </button>
                      )}
                      {j.status === "Invoiced" && <span className="text-xs text-[#F97316] font-semibold">Invoiced ✓</span>}
                      <button onClick={() => del(j.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`fs-delete-${j.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
export default FieldService;
